import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const StarRow = ({ value, onChange, testid }) => {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex space-x-1" data-testid={testid}>
      {[1, 2, 3, 4, 5].map((s) => (
        <button
          key={s}
          type="button"
          className={`text-2xl transition-colors cursor-pointer hover:scale-110 ${
            s <= (hover || value) ? 'text-yellow-400' : 'text-gray-400'
          }`}
          onClick={() => onChange(s)}
          onMouseEnter={() => setHover(s)}
          onMouseLeave={() => setHover(0)}
        >
          ★
        </button>
      ))}
    </div>
  );
};

const QuestionField = ({ question, value, onChange }) => {
  const { id, label, type, required, options = [] } = question;

  if (type === 'rating') {
    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        <StarRow value={value || 0} onChange={onChange} testid={`q-rating-${id}`} />
      </div>
    );
  }

  if (type === 'yes_no') {
    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        <div className="flex gap-3">
          {['Yes', 'No'].map((opt) => (
            <button
              key={opt}
              type="button"
              data-testid={`q-yesno-${id}-${opt.toLowerCase()}`}
              onClick={() => onChange(opt)}
              className={`px-5 py-2 rounded-md border-2 text-sm font-medium transition-colors ${
                value === opt
                  ? 'border-blue-600 bg-blue-50 text-blue-700'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              {opt}
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (type === 'multiple_choice') {
    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        <div className="space-y-2">
          {options.map((opt, i) => (
            <label
              key={i}
              className={`flex items-center gap-2 px-3 py-2 border rounded-md cursor-pointer ${
                value === opt ? 'border-blue-600 bg-blue-50' : 'border-gray-300'
              }`}
            >
              <input
                type="radio"
                name={id}
                checked={value === opt}
                onChange={() => onChange(opt)}
                data-testid={`q-mc-${id}-${i}`}
              />
              <span className="text-sm text-gray-700">{opt}</span>
            </label>
          ))}
        </div>
      </div>
    );
  }

  // text
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <textarea
        data-testid={`q-text-${id}`}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      />
    </div>
  );
};

const ServiceEvaluation = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [answers, setAnswers] = useState({});
  const [customerName, setCustomerName] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [overallRating, setOverallRating] = useState(0);
  const [additionalComments, setAdditionalComments] = useState('');

  useEffect(() => {
    const fetchService = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/services/${serviceId}/public`);
        setService(res.data);
      } catch (err) {
        setError('Service not found or unavailable');
      } finally {
        setLoading(false);
      }
    };
    fetchService();
  }, [serviceId]);

  const setAnswer = (qid, value) => setAnswers((prev) => ({ ...prev, [qid]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate required questions
    for (const q of service?.questions || []) {
      if (q.required) {
        const v = answers[q.id];
        if (v === undefined || v === null || v === '' || (q.type === 'rating' && !v)) {
          return setError(`Please answer: "${q.label}"`);
        }
      }
    }
    if (overallRating === 0) return setError('Please give an overall rating');

    setSubmitting(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/service-evaluations`, {
        service_id: service.id,
        customer_name: customerName || null,
        mobile_number: mobileNumber || null,
        answers,
        overall_rating: overallRating,
        additional_comments: additionalComments || null,
      });

      if (overallRating >= 4 && service?.organization?.google_review_url) {
        window.location.href = service.organization.google_review_url;
        return;
      }
      navigate(`/organization-feedback-success/${service.organization.id}`, {
        state: { rating: overallRating, fromService: true, evaluationId: res.data.id },
      });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit evaluation. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error && !service) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Service Not Found</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8" data-testid="service-evaluation-page">
      <div className="max-w-lg mx-auto">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="bg-white px-6 py-8 text-center border-b border-gray-200">
            {service?.organization?.logo ? (
              <img
                src={service.organization.logo}
                alt="Logo"
                className="h-12 object-contain inline-block mx-auto mb-4"
              />
            ) : (
              <div className="w-12 h-12 mx-auto mb-4 bg-gray-100 rounded-lg flex items-center justify-center">
                <span className="text-gray-600 text-xl font-bold">
                  {service?.organization?.name?.charAt(0) || 'S'}
                </span>
              </div>
            )}
            <h1 className="text-lg font-semibold text-gray-900" data-testid="service-name">
              {service?.name}
            </h1>
            {service?.department && (
              <p className="text-xs text-gray-500">{service.department.name}</p>
            )}
            {service?.description && (
              <p className="text-sm text-gray-600 mt-2">{service.description}</p>
            )}
          </div>

          <form onSubmit={handleSubmit} className="px-6 py-8 space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Your Name</label>
              <input
                type="text"
                data-testid="service-customer-name"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500"
                placeholder="Optional"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Mobile Number</label>
              <input
                type="tel"
                data-testid="service-mobile"
                value={mobileNumber}
                onChange={(e) => setMobileNumber(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500"
                placeholder="Optional"
              />
            </div>

            {(service?.questions || []).map((q) => (
              <QuestionField
                key={q.id}
                question={q}
                value={answers[q.id]}
                onChange={(v) => setAnswer(q.id, v)}
              />
            ))}

            <div className="pt-4 border-t">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Overall rating <span className="text-red-500">*</span>
              </label>
              <StarRow value={overallRating} onChange={setOverallRating} testid="service-overall-rating" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional comments
              </label>
              <textarea
                data-testid="service-comments"
                rows={3}
                value={additionalComments}
                onChange={(e) => setAdditionalComments(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500"
                placeholder="Optional"
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <button
              type="submit"
              data-testid="service-submit-btn"
              disabled={submitting}
              className="w-full bg-white text-blue-600 py-3 px-4 rounded-md font-medium border-2 border-blue-600 hover:bg-blue-50 disabled:opacity-50 transition-colors"
            >
              {submitting ? 'Submitting...' : 'Submit Evaluation'}
            </button>
          </form>

          <div className="bg-gray-50 px-6 py-4 text-center">
            <p className="text-xs text-gray-500">Thank you for sharing your feedback!</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceEvaluation;
